import { ApiError, type CustomerPaymentMethod, type PaymentMethodSetup, queryKeys } from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Switch,
  useToast,
} from '@mevaser/ui'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditCard, Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { api } from '../lib/api.js'
import { getStripe } from '../lib/stripe.js'

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function isExpired(card: CustomerPaymentMethod): boolean {
  const now = new Date()
  const thisMonth = now.getFullYear() * 12 + now.getMonth() // 0-indexed month
  const cardMonth = card.expYear * 12 + (card.expMonth - 1)
  return cardMonth < thisMonth
}

/**
 * Cards kept on file with the payment provider for recurring billing and
 * faster counter checkout (t0855) — only ever a provider reference, never raw
 * card data. Mirrors `PhoneNumbersCard`'s shape: a list card with an Add
 * button, a confirmation dialog for the destructive action.
 */
export function SavedCardsCard({
  customer,
  canWrite,
}: {
  customer: { id: string }
  canWrite: boolean
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<CustomerPaymentMethod | null>(null)

  const { data: cards } = useQuery({
    queryKey: queryKeys.customers.paymentMethods(customer.id),
    queryFn: () => api.get<CustomerPaymentMethod[]>(`/customers/${customer.id}/payment-methods`),
  })

  const invalidateCards = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.paymentMethods(customer.id) })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/customers/${customer.id}/payment-methods/${id}`),
    onSuccess: async () => {
      await invalidateCards()
      toast.success('Card removed')
      setRemoving(null)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="size-4" aria-hidden="true" /> Saved cards
        </CardTitle>
        {canWrite ? (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Plus className="size-3.5" aria-hidden="true" /> Add
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        {cards?.length ? (
          <ul className="divide-y divide-border">
            {cards.map((card) => (
              <li key={card.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="font-identifier">
                    {capitalize(card.brand)} •••• {card.last4}
                  </p>
                  <p className="text-2xs text-foreground-muted">
                    Expires {String(card.expMonth).padStart(2, '0')}/{card.expYear} · consent on{' '}
                    {new Date(card.consentAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                {isExpired(card) ? <Badge tone="danger">Expired</Badge> : null}
                {canWrite ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${card.brand} ending ${card.last4}`}
                    onClick={() => setRemoving(card)}
                  >
                    <Trash2 className="size-3.5" aria-hidden="true" />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={CreditCard}
              title="No saved cards"
              description="A card saved here can be used for recurring billing later."
            />
          </div>
        )}
      </CardContent>

      {canWrite ? (
        <AddCardDialog customerId={customer.id} open={adding} onOpenChange={setAdding} />
      ) : null}

      <Dialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove this card?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              {removing ? `${capitalize(removing.brand)} •••• ${removing.last4}` : 'This card'} is
              detached at Stripe and can't be charged again. This can't be undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => removing && remove.mutate(removing.id)}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

export function AddCardDialog({
  customerId,
  open,
  onOpenChange,
}: {
  customerId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const [consented, setConsented] = useState(false)
  const [setup, setSetup] = useState<PaymentMethodSetup | null>(null)
  const [startError, setStartError] = useState<string | undefined>()

  const startSetup = useMutation({
    mutationFn: () =>
      api.post<PaymentMethodSetup>(`/customers/${customerId}/payment-methods/setup-intent`, {}),
    onSuccess: (result) => setSetup(result),
    onError: (error: Error) => {
      if (error instanceof ApiError && error.status === 400) setStartError(error.message)
      else toast.error(error.message)
    },
  })

  const reset = () => {
    setConsented(false)
    setSetup(null)
    setStartError(undefined)
  }

  const close = () => {
    onOpenChange(false)
    reset()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Save a card on file</DialogTitle>
        </DialogHeader>
        {!setup ? (
          <>
            <DialogBody className="space-y-4">
              <p className="text-sm text-foreground-muted">
                The card itself is stored with our payment provider, not in this system — only a
                reference is kept here.
              </p>
              <div className="flex items-start gap-3 rounded-md border border-border p-2.5">
                <Switch checked={consented} onCheckedChange={setConsented} className="mt-0.5" />
                <span className="text-sm">
                  The customer has agreed that this card will be stored and may be charged later
                  without them present, including for recurring billing.
                </span>
              </div>
              {startError ? (
                <p className="text-sm text-danger" role="alert">
                  {startError}
                </p>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!consented || startSetup.isPending}
                onClick={() => {
                  setStartError(undefined)
                  startSetup.mutate()
                }}
              >
                {startSetup.isPending ? 'Starting…' : 'Continue'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <CardSetupPanel
            customerId={customerId}
            publishableKey={setup.publishableKey}
            clientSecret={setup.clientSecret}
            onSaved={close}
            onCancel={close}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Mounts Stripe.js against the setup intent's client secret and shows the
 *  card form. Its own component so `useStripe`/`useElements` — which only
 *  work inside `<Elements>` — have somewhere to live (mirrors
 *  `apps/web`'s `CardPaymentPanel`/`PayForm` split). */
function CardSetupPanel({
  customerId,
  publishableKey,
  clientSecret,
  onSaved,
  onCancel,
}: {
  customerId: string
  publishableKey: string
  clientSecret: string
  onSaved: () => void
  onCancel: () => void
}) {
  const stripePromise = useMemo(() => getStripe(publishableKey), [publishableKey])

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CardSetupForm customerId={customerId} onSaved={onSaved} onCancel={onCancel} />
    </Elements>
  )
}

function CardSetupForm({
  customerId,
  onSaved,
  onCancel,
}: {
  customerId: string
  onSaved: () => void
  onCancel: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [isWorking, setIsWorking] = useState(false)
  // Set once Stripe has confirmed the card — a retry after that point only
  // re-posts the attach request, it never re-runs `confirmSetup`.
  const [confirmedSetupIntentId, setConfirmedSetupIntentId] = useState<string | null>(null)

  const attach = useMutation({
    mutationFn: (setupIntentId: string) =>
      api.post(`/customers/${customerId}/payment-methods`, { setupIntentId, consent: true }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.customers.paymentMethods(customerId),
      })
      onSaved()
    },
    onError: (err: Error) => setError(err.message),
  })

  const handleSave = async () => {
    if (!stripe || !elements) return
    setError(null)
    setIsWorking(true)
    try {
      let setupIntentId = confirmedSetupIntentId
      if (!setupIntentId) {
        const result = await stripe.confirmSetup({ elements, redirect: 'if_required' })
        if (result.error) {
          setError(result.error.message ?? 'The card was not accepted. Please try again.')
          return
        }
        setupIntentId = result.setupIntent.id
        setConfirmedSetupIntentId(setupIntentId)
      }
      await attach.mutateAsync(setupIntentId)
    } catch {
      // `attach`'s onError already recorded the message in `error` state —
      // this just stops the rejection from surfacing as unhandled.
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <>
      <DialogBody className="space-y-4">
        <PaymentElement />
        {error ? (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} disabled={isWorking}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleSave()}
          disabled={!stripe || !elements || isWorking}
        >
          {isWorking ? 'Saving…' : confirmedSetupIntentId ? 'Try again' : 'Save card'}
        </Button>
      </DialogFooter>
    </>
  )
}
